import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import cookieService from '../utils/CookieService';
import { authservices } from '../APIServices/AuthService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearEntitlement, fetchUserEntitlement } from '../store/slices/userEntitlementSlice';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(cookieService.getToken());
  const [role, setRole] = useState(null);
  const [username, setUsername] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!cookieService.getToken());
  const dispatch = useAppDispatch();
  const { data: entitlementData, loading: entitlementLoading } = useAppSelector((state) => state.userEntitlement);
  const entitlementInitialized = useRef(false);

  const setToken = async (newToken) => {
    if (newToken) {
      setTokenState(newToken);
      setIsAuthenticated(true);
      cookieService.setToken(newToken);
      
      // Fetch user entitlement when new token is set (e.g., on login)
      if (!entitlementData && !entitlementLoading && !entitlementInitialized.current) {
        entitlementInitialized.current = true;
        dispatch(fetchUserEntitlement()).catch((error) => {
          console.error('Failed to fetch user entitlement:', error);
          entitlementInitialized.current = false;
        });
      }
    } else {
      logout();
    }
  };

  const logout = async () => {
    // Call logout API if user ID is available
    const userId = localStorage.getItem("userId");
    if (userId) {
      try {
        await authservices.logout(userId);
      } catch (error) {
        console.error('Error calling logout API:', error);
        // Continue with local logout even if API call fails
      }
    }
    
    // Clear local state
    setTokenState(null);
    setRole(null);
    setUsername(null);
    setIsAuthenticated(false);
    cookieService.clearAllCookies();
    
    // Clear stored user ID
    localStorage.removeItem("userId");
    
    // Clear user entitlement from Redux
    dispatch(clearEntitlement());
    
    // Reset entitlement initialization flag
    entitlementInitialized.current = false;

    // Force a hard redirect to clear any in-memory auth state
    window.location.href = '/login';
  };

  // Initialize on mount - fetch entitlement if token exists
  useEffect(() => {
    const initializeAuth = async () => {
      const currentToken = cookieService.getToken();
      console.log('AuthProvider: Initializing auth, token exists:', !!currentToken);
      
      if (currentToken) {
        console.log('AuthProvider: Token is set, initializing state');
        setTokenState(currentToken);
        setIsAuthenticated(true);
        
        // Fetch user entitlement on mount/refresh (only once)
        if (!entitlementInitialized.current) {
          entitlementInitialized.current = true;
          console.log('AuthProvider: Fetching user entitlement on mount...');
          dispatch(fetchUserEntitlement())
            .then(() => {
              console.log('AuthProvider: User entitlement loaded successfully');
            })
            .catch((error) => {
              console.error('AuthProvider: Failed to fetch user entitlement:', error);
              entitlementInitialized.current = false; // Allow retry on error
            });
        }
      } else {
        console.log('AuthProvider: No token found');
        setIsAuthenticated(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Update role/username from entitlement
  useEffect(() => {
    if (!entitlementData) return;
    const entitlementRole = entitlementData?.role?.name?.replace(/^ROLE_/, "");
    const displayName =
      entitlementData?.email ||
      [entitlementData?.firstName, entitlementData?.lastName].filter(Boolean).join(" ") ||
      null;
    setRole(entitlementRole || null);
    setUsername(displayName);
  }, [entitlementData]);

  const value = {
    token,
    role,
    username,
    isAuthenticated,
    setToken,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
